import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { usersApi } from "api/User/api.ts";
import { composeGetMeQueryOptions } from "api/User/queryOptions.ts";
import { AxiosError } from "axios";
import { RoleEnum } from "types/enums.ts";
import { ApiErrorResponse, ApiResponse } from "types/models.ts";

import VerifyEmailRequest = App.Http.Requests.User.VerifyEmailRequest;
import CheckEmailRequest = App.Http.Requests.User.CheckEmailRequest;
import CheckEmailResponse = App.Http.Responses.User.CheckEmailResponse;
import ConfirmRegistrationRequest = App.Http.Requests.User.ConfirmRegistrationRequest;
import ConfirmRegistrationResendRequest = App.Http.Requests.User.ConfirmRegistrationResendRequest;
import RegistrationRequest = App.Http.Requests.User.RegistrationRequest;
import VerifyEmailResendRequest = App.Http.Requests.User.VerifyEmailResendRequest;
import ForgotPasswordRequest = App.Http.Requests.User.ForgotPasswordRequest;
import ResetPasswordRequest = App.Http.Requests.User.ResetPasswordRequest;
// import GetUserInvitationsRequest = App.Http.Requests.User.GetUserInvitationsRequest;
import GetUserResponse = App.Http.Responses.User.GetUserResponse;
import VerifyEmailLinkRequest = App.Http.Requests.User.VerifyEmailLinkRequest;
import GetUsersRequest = App.Http.Requests.User.GetUsersRequest;
import GetUsersResponse = App.Http.Responses.User.GetUsersResponse;
import GetTenantsResponse = App.Http.Responses.Tenant.GetTenantsResponse;
import GetMeRequest = App.Http.Requests.User.GetMeRequest;
import GetUserConversionsApiTokenResponse = App.Http.Responses.User.GetUserConversionsApiTokenResponse;
import UpdateUserRequest = App.Http.Requests.User.UpdateUserRequest;
import UpdateMeRequest = App.Http.Requests.User.UpdateMeRequest;
import ChangePasswordRequest = App.Http.Requests.User.ChangePasswordRequest;

export interface VerifyEmailLinkRequestWithId extends VerifyEmailLinkRequest {
    id: string;
    hash: string;
    tenant_id?: string;
    role?: RoleEnum;
}

export interface VerifyEmailLinkRequestWithEmail extends VerifyEmailLinkRequestWithId {
    email?: string;
    password: string;
    password_confirmation: string;
}

// Hook to verify user email
export const useVerifyUserEmailMutation = (
    options: UseMutationOptions<GetUserResponse, AxiosError<ApiErrorResponse>, VerifyEmailRequest> = {}
) => {
    return useMutationApi<GetUserResponse, VerifyEmailLinkRequestWithEmail>({
        mutationKey: ["verifyEmail"],
        mutationFn: (request) => usersApi.verifyEmail(request),
        ...options,
    });
};

// Hook to verify email link
export const useVerifyUserEmailLinkQuery = (request: VerifyEmailLinkRequestWithId, options = {}) => {
    return useQueryApi<ApiResponse<null>>({
        queryKey: ["verifyEmailLink", { ...request }],
        queryFn: () => usersApi.verifyEmailLink(request),
        // This checks a one-time link's validity — the persisted query cache (localStorage) can
        // still hold a "valid" result from before the link was used, which would briefly show the
        // password form again on refresh even though the account is already activated. Force a
        // revalidation on every mount instead of trusting the default 30s staleTime.
        staleTime: 0,
        ...options,
    });
};

// Hook to get users list
export const useGetUsersListQuery = (request: GetUsersRequest, options = {}) => {
    return useQueryApi<GetUsersResponse>({
        queryKey: ["users", { ...request }],
        queryFn: () => usersApi.getList(request),
        ...options,
    });
};

// Hook to register a user
export const useRegisterUserMutation = (
    options: UseMutationOptions<GetUserResponse, AxiosError<ApiErrorResponse>, RegistrationRequest> = {}
) => {
    return useMutationApi<GetUserResponse, RegistrationRequest>({
        mutationKey: ["registerUser"],
        mutationFn: (request) => usersApi.register(request),
        ...options,
    });
};

// Hook to check whether an email is already registered
export const useCheckEmailMutation = (
    options: UseMutationOptions<CheckEmailResponse | null, AxiosError<ApiErrorResponse>, CheckEmailRequest> = {}
) => {
    return useMutationApi<CheckEmailResponse | null, CheckEmailRequest>({
        mutationKey: ["checkEmail"],
        mutationFn: (request: CheckEmailRequest) => usersApi.checkEmail(request),
        ...options,
    });
};

// Hook to resend the confirm-registration email
export const useConfirmRegistrationResendMutation = (
    options: UseMutationOptions<null, AxiosError<ApiErrorResponse>, ConfirmRegistrationResendRequest> = {}
) => {
    return useMutationApi<null, ConfirmRegistrationResendRequest>({
        mutationKey: ["confirmRegistrationResend"],
        mutationFn: (request: ConfirmRegistrationResendRequest) => usersApi.confirmRegistrationResend(request),
        ...options,
    });
};

// Hook to confirm a tenant join for an existing verified user
export const useConfirmRegistrationQuery = (request: ConfirmRegistrationRequest, options = {}) => {
    return useQueryApi<null>({
        queryKey: ["confirmRegistration", { ...request }],
        queryFn: () => usersApi.confirmRegistration(request),
        ...options,
    });
};

// Hook to get specific user
export const useGetUserQuery = (userId: string, options = {}) => {
    return useQueryApi<GetUserResponse>({
        queryKey: ["getUser", userId],
        queryFn: () => usersApi.getUser(userId),
        ...options,
    });
};

// Hook to get current user (me)
export const useGetMeQuery = (request: GetMeRequest, options = {}) => {
    return useQueryApi<GetUserResponse>({
        ...composeGetMeQueryOptions(request),
        ...options,
    });
};

// Hook to resend email verification
export const useResendVerifyEmailMutation = (
    options: UseMutationOptions<null, AxiosError<ApiErrorResponse>, VerifyEmailResendRequest> = {}
) => {
    return useMutationApi<null, VerifyEmailResendRequest>({
        mutationKey: ["resendVerifyEmail"],
        mutationFn: (request: VerifyEmailResendRequest) => usersApi.verifyEmailResend(request),
        ...options,
    });
};

// Hook for forgot password
export const useForgotPasswordMutation = (
    options: UseMutationOptions<null, AxiosError<ApiErrorResponse>, ForgotPasswordRequest> = {}
) => {
    return useMutationApi<null, ForgotPasswordRequest>({
        mutationKey: ["forgotPassword"],
        mutationFn: (request: ForgotPasswordRequest) => usersApi.forgotPassword(request),
        ...options,
    });
};

// Hook to reset password
export const useResetPasswordMutation = (
    options: UseMutationOptions<null, AxiosError<ApiErrorResponse>, ResetPasswordRequest> = {}
) => {
    return useMutationApi<null, ResetPasswordRequest>({
        mutationKey: ["resetPassword"],
        mutationFn: (request: ResetPasswordRequest) => usersApi.resetPassword(request),
        ...options,
    });
};

// Hook to get user's tenants
export const useGetUserTenantsQuery = (userId: string, options = {}) => {
    return useQueryApi<GetTenantsResponse>({
        queryKey: ["getUserTenants", userId],
        queryFn: () => usersApi.getUserTenants(userId),
        ...options,
    });
};

// Hook to get user conversion api token
export const useGetConversionApiToken = (options = {}) => {
    return useQueryApi<GetUserConversionsApiTokenResponse>({
        queryKey: ["getConversionApiToken"],
        queryFn: () => usersApi.getConversionApiToken(),
        ...options,
    });
};

// Hook to regenerate user conversion api token
export const useRegenerateConversionApiTokenMutation = (
    options: UseMutationOptions<GetUserConversionsApiTokenResponse, AxiosError<ApiErrorResponse>, void> = {}
) => {
    return useMutationApi<GetUserConversionsApiTokenResponse, void>({
        mutationKey: ["regenerateConversionApiToken"],
        mutationFn: () => usersApi.regenerateConversionApiToken(),
        ...options,
    });
};

export const useUpdateMeMutation = (
    options: UseMutationOptions<GetUserResponse, AxiosError<ApiErrorResponse>, UpdateMeRequest> = {}
) => {
    return useMutationApi<GetUserResponse, UpdateMeRequest>({
        mutationKey: ["updateMe"],
        mutationFn: (request) => usersApi.updateMe(request),
        ...options,
    });
};

export const useChangePasswordMutation = (
    options: UseMutationOptions<void, AxiosError<ApiErrorResponse>, ChangePasswordRequest> = {}
) => {
    return useMutationApi<void, ChangePasswordRequest>({
        mutationKey: ["changePassword"],
        mutationFn: (request) => usersApi.changePassword(request),
        ...options,
    });
};

export interface UpdateUserVariables {
    userId: string;
    request: UpdateUserRequest;
}

export const useUpdateUserMutation = (
    options: UseMutationOptions<GetUserResponse, AxiosError<ApiErrorResponse>, UpdateUserVariables> = {}
) => {
    return useMutationApi<GetUserResponse, UpdateUserVariables>({
        mutationKey: ["updateUser"],
        mutationFn: ({ userId, request }) => usersApi.update(userId, request),
        ...options,
    });
};
